import Div from '@ui/layout/Div'
import Form from '@ui/elements/Form'
import Alert from '@ui/elements/Alert'
import sterilizationCompanyCallFormFields, { sterilizationCompanyCallSaveUrl } from "@form/sterilizationCompanyCallFormFields"
import { useDispatch, useSelector } from "react-redux"
import useStoreUpdate from "@hook/useStoreUpdate"
import useToggle from "@hook/useToggle"
import useMainData from '@hook/useMainData'
import useFormData from "@hook/useFormData"
import useCostCalculator from "@hook/useCostCalculator"
import useGoogleRecaptchaTokenizer from "@hook/useGoogleRecaptchaTokenizer"
import { updateSterilizationCompanyCallFormObject, updateSterilizationPayment } from "@store/slice/sterilizationSlice"
import { post } from '@util/xhrAPI'
import { updateNotification, updateValidationPage } from '@store/slice/mainSlice'
import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { func } from 'prop-types'
import { useNavigate } from 'react-router'
import { ROLE_PUBLIC } from "@router/Routes"

const CompanySterilizationCallForm = ({ nextStep }) => {
  const { sterilizationCompanyCallFormObject, errors } = useSelector(state => state.sterilization)
  const { data, mission, missionBillings } = useSelector(state => state.main)
  const { paymentConfig } = mission
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [updateFormObject, updateFormObjectProp] = useStoreUpdate(dispatch, updateSterilizationCompanyCallFormObject, sterilizationCompanyCallFormObject)
  const [isLoading, toggleLoading] = useToggle(false)
  const [userCoordinates, setUserCoordinates] = useState(null)
  const [t] = useTranslation('sterilization')
  const renderFormData = useFormData()
  const loadData = useMainData()
  const [cost, setCost] = useState(null)
  const [unitsToBill, setUnitsToBill] = useState(null)
  const [currentTarget, setTarget] = useState(null)
  const calculateCost = useCostCalculator()
  const { callType } = sterilizationCompanyCallFormObject
  const [invokeChallenge, appendScript] = useGoogleRecaptchaTokenizer()

  const isSurfaceBased = () => {
    const billingType = data.billingTypes.filter(billingType => billingType.value === paymentConfig[`${callType}Payment`]?.billingType)[0]
    return billingType?.isSurfaceBased
  }

  const renderCost = () => {
    if (!cost || 1 !== parseInt(paymentConfig[`${callType}Payment`]?.isBilled)) return <></>
    return <>
      <Alert
        message={t('form.nests_number_help', { unitsNumber: sterilizationCompanyCallFormObject.unitsNumber, cost: cost })}
        type='is-info'
      />
    </>
  }

  const generateFormStep = step => {
    return sterilizationCompanyCallFormFields(
      step,
      sterilizationCompanyCallFormObject,
      updateFormObject,
      updateFormObjectProp,
      errors,
      saveForm,
      callType ? 1 === parseInt(paymentConfig[`${callType}Payment`]?.isBilled) : false,
      callType ? isSurfaceBased() : false,
      isLoading,
      t,
      data,
      cost,
      renderCost,
      mission
    )
  }

  const fetchInitialData = () => {
    const dataObject = {
      cities: 'cities',
      districts: 'districts',
      billingTypes: 'billingTypes'
    }
    loadData(dataObject)
  }

  const saveForm = async () => {
    toggleLoading()
    const token = await invokeChallenge(updateFormObjectProp)
    const callObject = { ...sterilizationCompanyCallFormObject, 'g-recaptcha-response': token }
    post(sterilizationCompanyCallSaveUrl, renderFormData(callObject, ['userCoordinates']), response => {
      const { code, sterilizationCall, sterilizationOrder, paymentModes, paymentPage, validationPage, errors, isToPay } = response
      if (200 === code) {
        dispatch(updateNotification({ type: 'is-success', message: t('messages.call_created_successfuly') }))
        if (isToPay && sterilizationOrder && sterilizationOrder.billedAmount) {
          dispatch(updateSterilizationPayment({ sterilizationOrder, paymentModes, paymentPage }))
          navigate(ROLE_PUBLIC['paymentPage'])
          window.localStorage.setItem('sterilizationOrder', sterilizationOrder.reference)
        } else {
          if (validationPage) {
            dispatch(updateValidationPage(validationPage))
            navigate(ROLE_PUBLIC['publicValidation'])
          } else {
            dispatch(updateSterilizationCompanyCallFormObject(sterilizationCall))
            window.localStorage.setItem('lastCompanyCall', sterilizationCall.id)
            nextStep('buildings')
          }
        }

      } else {
        dispatch(updateNotification({ type: 'is-danger', message: t(`messages.${errors.length ? errors[0] : 'errors_encountered'}`) }))
      }

      toggleLoading()
    })
  }

  useEffect(() => {
    updateFormObjectProp('mission', mission.id)
    fetchInitialData()
  }, [])

  useEffect(() => {
    if (window.innerWidth <= 768) { //get user location if on mobile
      navigator.geolocation.getCurrentPosition(function (position) {
        setUserCoordinates({ lat: position.coords.latitude, lng: position.coords.longitude })
      })
    }
    appendScript()
  }, [])

  useEffect(() => {
    if (userCoordinates) {
      updateFormObjectProp('userCoordinates', userCoordinates)
    }
  }, [userCoordinates])

  useEffect(() => {
    if (
      sterilizationCompanyCallFormObject.unitsNumber && (
        sterilizationCompanyCallFormObject.unitsNumber != unitsToBill || sterilizationCompanyCallFormObject.callType != currentTarget
      )
    ) {
      setCost(calculateCost(
        parseFloat(sterilizationCompanyCallFormObject.unitsNumber),
        Object.values(missionBillings),
        sterilizationCompanyCallFormObject.callType
      ))
      setTarget(sterilizationCompanyCallFormObject.callType)
      setUnitsToBill(sterilizationCompanyCallFormObject.unitsNumber)
    }

  }, [sterilizationCompanyCallFormObject])

  return (
    <>
      <Form cssClass='citizen-sterilization-call'>
        <Div cssClass='columns is-multiline'>
          {generateFormStep('call')}
        </Div>

        <Div cssClass='columns is-multiline'>
          <Div cssClass='column is-6 is-multiline'>
            {generateFormStep('establishmentResponsible')}
          </Div>
          <Div cssClass='column is-6 is-multiline'>
            {generateFormStep('callResponsible')}
          </Div>
        </Div>

        <Div cssClass='columns is-multiline'>
          {generateFormStep('callLocation')}
        </Div>
        {'company' === sterilizationCompanyCallFormObject.callType && <>
          <Div cssClass='columns is-multiline'>
            {generateFormStep('company')}
          </Div>
        </>}

        <Div cssClass='columns is-multiline'>
          {generateFormStep('callChecks')}
        </Div>
      </Form>
    </>
  )
}

CompanySterilizationCallForm.propTypes = {
  nextStep: func
}

export default CompanySterilizationCallForm
